import { useTeamsAuth } from '@teams/hooks/useTeamsAuth';
import * as S from '@teams/Root.styles';
import { teamsPaths } from '@teams/routes/AppRoutes';
import { BaseScrollGlobalStyle } from '@toggle/design-system';
import React, { FC, Suspense, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';

import { Loader } from '~/components/loader/Loader';
import { ToastsLayout } from '~/components/toasts-layout/ToastsLayout';
import { useAuthStore } from '~/stores/use-auth/store/authStore';
import { getSession, useAuth } from '~/stores/use-auth/useAuth';

// Detect whether we're actually running inside the Teams client.
// Outside Teams (direct browser / dev mode), window.parent === window.
const isInsideTeams = () =>
  typeof window !== 'undefined' && window.parent !== window;

export const Root: FC = () => {
  const { authProcessing, initStores, setAuthProcessing } = useAuth();
  const isAuthenticated = useAuthStore(state => !!state.token);
  const { initiateLogin } = useTeamsAuth();
  const location = useLocation();

  const isAuthPopup = location.pathname === teamsPaths.authPopup;
  useEffect(() => {
    const initSession = async () => {
      if (isAuthPopup) {
        setAuthProcessing(false);
        return;
      }

      try {
        // Cookie-based session works in Teams desktop client and direct browser access
        const session = await getSession();

        if (session) {
          await initStores(session);
          setAuthProcessing(false);
          return;
        }

        if (isInsideTeams()) {
          // Inside Teams but no cookie session — open the Teams auth popup.
          // authProcessing is cleared by the login callbacks, not here.
          initiateLogin({
            onSuccess: async authData => {
              try {
                await initStores(authData);
              } finally {
                setAuthProcessing(false);
              }
            },
            onFailure: () => {
              setAuthProcessing(false);
            },
          });
        } else {
          // Outside Teams (dev browser) and no session — show auth popup login page directly
          setAuthProcessing(false);
        }
      } catch {
        // getSession() or initStores() threw — ensure the loading state is always cleared
        setAuthProcessing(false);
      }
    };

    initSession();
  }, []);

  if (authProcessing && !isAuthPopup) {
    return <Loader />;
  }

  // Outside Teams with no session: redirect to the login page
  if (!isAuthenticated && !isAuthPopup) {
    return <Navigate to={teamsPaths.authPopup} replace />;
  }

  return (
    <>
      <BaseScrollGlobalStyle />
      <S.AppWrapper>
        <Suspense fallback={<Loader />}>
          <Outlet />
        </Suspense>
      </S.AppWrapper>
      <ToastsLayout />
    </>
  );
};
